"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { gsap } from "gsap"
import axios from "axios"
import { API_BASE_URL } from "../utils/Constant"
import HomeNavbar from "./HomeNavbar"
import Footer from "./Footer"

const ForgotPassword = () => {
  const [email, setEmail] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [otp, setOtp] = useState("")
  const [message, setMessage] = useState("")
  const [messageType, setMessageType] = useState("") // "success" or "error"
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false)
  const navigate = useNavigate()

  const formRef = useRef(null)
  const modalRef = useRef(null)

  useEffect(() => {
    // Form animation
    const formElement = formRef.current
    const formChildren = formElement?.querySelectorAll(".form-item") || []
    gsap.set(formChildren, { y: 50, opacity: 0 })

    const formObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(formChildren, {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.2,
              ease: "power3.out",
            })
            formObserver.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.3 }
    )

    if (formElement) formObserver.observe(formElement)

    // Modal animation (if open)
    const modalElement = modalRef.current
    if (isOtpModalOpen && modalElement) {
      const modalChildren = modalElement.querySelectorAll(".modal-item") || []
      gsap.set(modalChildren, { y: 20, opacity: 0 })
      gsap.to(modalChildren, {
        y: 0,
        opacity: 1,
        duration: 0.5,
        stagger: 0.1,
        ease: "power3.out",
      })
    }

    return () => {
      if (formElement) formObserver.unobserve(formElement)
    }
  }, [isOtpModalOpen])

  const validatePassword = (password) => {
    const minLength = 8
    const hasUpperCase = /[A-Z]/.test(password)
    const hasLowerCase = /[a-z]/.test(password)
    const hasNumber = /[0-9]/.test(password)
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)
    return password.length >= minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar
  }

  const handleRequestOtp = async (e) => {
    e.preventDefault()
    if (!validatePassword(newPassword)) {
      setMessageType("error")
      setMessage(
        "Password must be at least 8 characters long and include uppercase, lowercase, a number, and a special character."
      )
      return
    }
    if (newPassword !== confirmPassword) {
      setMessageType("error")
      setMessage("Passwords do not match.")
      return
    }
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/forgot-password`, { email, password: newPassword })
      if (response.status === 200) {
        setMessageType("success")
        setMessage("OTP sent to your email.")
        setIsOtpModalOpen(true)
      }
    } catch (error) {
      setMessageType("error")
      setMessage(error.response?.data?.message || "Failed to send OTP.")
    }
  }

  const handleVerifyOtp = async (e) => {
    e.preventDefault()
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/verify-otp-reset-password`, { email, otp })
      if (response.status === 200) {
        setMessageType("success")
        setMessage("Password reset successfully.")
        setIsOtpModalOpen(false)
        setTimeout(() => navigate("/auth/login"), 3000)
      }
    } catch (error) {
      setMessageType("error")
      setMessage(error.response?.data?.message || "Invalid OTP.")
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      <HomeNavbar />
      <section className="flex-grow flex items-center justify-center py-16 px-4 md:px-6 lg:px-8">
        <div ref={formRef} className="max-w-lg w-full bg-slate-700/50 backdrop-blur-sm p-8 rounded-xl">
          <h2 className="form-item text-2xl md:text-3xl font-bold text-center mb-10">Forgot Password</h2>
          {message && (
            <div
              className={`form-item mb-6 p-4 rounded-lg text-center font-semibold ${
                messageType === "success" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
              }`}
            >
              {message}
            </div>
          )}
          <form onSubmit={handleRequestOtp} className="space-y-6">
            <div className="form-item">
              <label htmlFor="email" className="block text-slate-200 font-medium mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-6 py-4 text-lg bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your email"
              />
            </div>
            <div className="form-item">
              <label htmlFor="newPassword" className="block text-slate-200 font-medium mb-2">
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="w-full px-6 py-4 text-lg bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new password"
              />
            </div>
            <div className="form-item">
              <label htmlFor="confirmPassword" className="block text-slate-200 font-medium mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-6 py-4 text-lg bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Confirm new password"
              />
            </div>
            <button
              type="submit"
              className="form-item w-full py-4 px-8 text-lg font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Reset Password
            </button>
          </form>
        </div>
      </section>
      <Footer />

      {isOtpModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div ref={modalRef} className="bg-slate-700/50 backdrop-blur-sm p-6 rounded-lg w-full max-w-sm">
            <h2 className="modal-item text-xl font-bold mb-4 text-center">Enter OTP</h2>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="modal-item w-full px-4 py-3 text-lg bg-slate-800 text-white border border-slate-600 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter 6-digit OTP"
            />
            <button
              onClick={handleVerifyOtp}
              className="modal-item w-full py-3 px-6 text-lg font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Verify OTP
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ForgotPassword